import React, { PureComponent } from 'react';
import { Link } from 'react-router';
import styled, { keyframes, css } from 'styled-components';

import { getRequest } from '../../../api/ApiService';
import Auth from '../../../routes/Auth';
import Teams from '../../../api/Teams';
import EventEmitter from '../../../services/event-emitter';

import CopyIcon from '../../../containers/assets/onboarding-copy.png';

// PDF for download. Served as a static asset from /public/ rather
// than bundled through webpack (webpack 1.x doesn't have a PDF
// loader configured). Save the PDF at:
//   public/team-contract-template.pdf
// and it will be served at the URL below.
const TEAM_CONTRACT_PDF_URL = '/team-contract-template.pdf';

// Round 1 onboarding checklist. Single consolidated welcome page
// at /onboarding/welcome/intro?round=1. Behavior contract:
//   - 3 visible top-level steps. Conduct Research is parent-only
//     (auto-completes when all substeps are checked).
//   - 5 countable items total drive the progress bar + success
//     banner.
//   - First incomplete top-level step gets the blue active highlight.
//   - "(view teammates)" inline opens a popup with the team roster
//     + copy-email controls.
//   - "View the team contract template" opens a popup showing the
//     contract with a Download button.
//   - Other link destinations route into the existing app pages,
//     carrying ?round=N forward.
//
// Persistence: localStorage, keyed per user + round. Swap for a
// backend endpoint once one exists.

// ---------- copy ---------------------------------------------------

const COMPLETION_COPY = {
  icon: '✓',
  title: "You're ready for the round.",
  description:
    'Great work. Check back here next round for your updated checklist.',
};

// IDs that count toward the progress bar / success banner.
// `conduct-research` is parent-only.
const COUNTABLE_IDS = [
  'team-outreach',
  'review-case-study',
  'review-round-goals',
  'analyze-customers',
  'make-decisions',
];

const TOTAL_COUNTABLE = COUNTABLE_IDS.length;

const storageKey = () => {
  const uid = Auth.getUserId();
  return `onboarding:checklist:round1:${uid || 'anon'}`;
};

// Role-intro screen: shown BEFORE the checklist on first load so the
// student sees what role they've been assigned and what they're
// responsible for this round. Dismissal persists per user + role so
// the intro doesn't reappear on refresh (but does resurface if the
// user's role somehow changes).
export const roleIntroStorageKey = () => {
  const uid = Auth.getUserId();
  let role = '';
  try {
    role = (Auth.getRole() || '').toString().toUpperCase();
  } catch (e) {
    role = '';
  }
  return `onboarding:role-intro:round1:${uid || 'anon'}:${role || 'unknown'}`;
};

// Read the persisted "has the student clicked through the role-intro
// screen" flag. Consumed by StudentLeftBar to decide whether to hide
// non-Welcome nav items while the intro is still on screen.
export const isRoleIntroDismissed = () => {
  try {
    return window.localStorage.getItem(roleIntroStorageKey()) === '1';
  } catch (e) {
    return false;
  }
};

// Event name for cross-component notification. Fires when the student
// clicks Continue on the role-intro screen so consumers like
// StudentLeftBar can react immediately (their own re-render won't be
// triggered by localStorage writes alone).
export const ROLE_INTRO_DISMISSED_EVENT = 'role-intro-dismissed';

// Role → intro copy. Labels match how the user wrote them (simple
// functional names for students), which are shorter than the display
// names on join-page.js. Descriptions are paraphrased from the
// product spec, kept concise and college-student-friendly.
const ROLE_INTRO_CONTENT = {
  CEO: {
    label: 'CEO',
    description:
      "You own the big picture. Build the financial projections for investors, and make sure every team decision is made and posted before the deadline. You can also step in to make, modify, or override any teammate's decision.",
  },
  PD: {
    label: 'Product',
    description:
      'Your job is to design and launch products customers actually want.',
  },
  PM: {
    label: 'Marketing',
    description:
      "Set the product's price, then choose which promotional and sales channels to reach customers, along with how much to invest in each. You can't post decisions until your Product Manager creates a product.",
  },
  OP: {
    label: 'Operations',
    description:
      'Manufacture the products, and manage inventory levels and locations to keep supply flowing.',
  },
  FI: {
    label: 'Finance',
    description:
      "Manage the company's money. Raise capital from investors to fund product, marketing, and operations decisions, and return capital to investors when the time is right.",
  },
  FN: {
    label: 'Finance',
    description:
      "Manage the company's money. Raise capital from investors to fund product, marketing, and operations decisions, and return capital to investors when the time is right.",
  },
};

const getCurrentRoleCode = () => {
  try {
    return (Auth.getRole() || '').toString().toUpperCase();
  } catch (e) {
    return '';
  }
};

// Team names are commonly stored as a bare number ("4", "12").
// Prefix with "Team " unless the name already starts with that
// word so we don't double-prefix things like "Team Northstar".
const formatTeamName = (name) => {
  const str = (name == null ? '' : String(name)).trim();
  if (!str) return '';
  return /^team\s/i.test(str) ? str : `Team ${str}`;
};

// Auth.getRole() returns one of: ceo, pd, pm, op, fi, fn.
// Map to the analysis route prefix.
const getRoleForAnalysisRoute = () => {
  let role = '';
  try {
    role = (Auth.getRole() || '').toString().toLowerCase();
  } catch (e) {
    role = '';
  }
  // `fi` and `fn` both exist in older code paths; analysis routes
  // are registered as /fn. Normalize.
  if (role === 'fi') role = 'fn';
  // Fall back to /ceo if the role isn't set — the page exists for
  // every role, so any of them works; CEO is the safest default.
  return role || 'ceo';
};

// Display label for the user's current role (rotates per round via
// Auth.getRole()). Matches the labels shown on join-page.js. Returns
// '' if the role can't be determined so callers can skip rendering.
const ROLE_DISPLAY_NAMES = {
  CEO: 'CEO',
  PD: 'Product Management',
  PM: 'Product Marketing',
  OP: 'Operations',
  FI: 'Finance',
  FN: 'Finance',
};

const getRoleDisplayName = () => {
  let role = '';
  try {
    role = (Auth.getRole() || '').toString().toUpperCase();
  } catch (e) {
    role = '';
  }
  return ROLE_DISPLAY_NAMES[role] || '';
};

// ---------- styles -------------------------------------------------

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const fadeIn = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;

const Styled = {};

// Tightened from the original design spec so the whole checklist
// fits one viewport without scrolling. The proportions still match
// the reference, just compressed across the board.
// Negative margins are applied on left/right/bottom only — the top
// stays at 0 so the page title doesn't slip behind the top nav bar.
Styled.Root = styled.section`
  background: #FAFBFD;
  min-height: 100%;
  margin: 0 -40px -40px;
  padding: 64px 32px 24px;
  font-family: 'Mukta Vaani', Lato, sans-serif;
  color: #0F172A;
`;

Styled.Header = styled.div`
  max-width: 760px;
  margin: 0 auto 16px;
  text-align: center;
`;

Styled.Title = styled.h1`
  margin: 0 0 14px;
  font-family: 'Mukta Vaani', Lato, sans-serif;
  font-size: 40px;
  font-weight: 800;
  letter-spacing: -0.025em;
  line-height: 1.1;
  color: #0F172A;
`;

Styled.Subhead = styled.p`
  margin: 0 0 14px;
  font-size: 16px;
  color: #475569;
  line-height: 1.4;
`;

Styled.ProgressTrack = styled.div`
  width: 96px;
  height: 4px;
  margin: 0 auto;
  background: #E2E8F0;
  border-radius: 999px;
  overflow: hidden;
`;

Styled.ProgressFill = styled.div`
  height: 100%;
  background: #10B981;
  border-radius: 999px;
  width: ${p => (p.pct || 0)}%;
  transition: width 400ms ease;

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

// ---- role intro screen styles ------------------------------------

Styled.IntroCard = styled.div`
  max-width: 720px;
  margin: 40px auto 0;
  padding: 44px 40px 40px;
  background: #FFFFFF;
  border: 1px solid #E2E8F0;
  border-radius: 20px;
  text-align: center;
  animation: ${fadeUp} 400ms ease both;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

Styled.IntroSubhead = styled.p`
  margin: 0 0 28px;
  font-size: 18px;
  color: #475569;
  line-height: 1.5;
`;

Styled.RoleBadge = styled.div`
  display: inline-block;
  padding: 10px 22px;
  margin: 0 0 24px;
  background: #EFF6FF;
  border: 2px solid #2563EB;
  border-radius: 999px;
  color: #2563EB;
  font-family: 'Mukta Vaani', Lato, sans-serif;
  font-weight: 800;
  font-size: 20px;
  letter-spacing: 0.3px;
`;

Styled.IntroDescription = styled.p`
  margin: 0 auto 32px;
  max-width: 560px;
  font-size: 18px;
  line-height: 1.6;
  color: #0F172A;
`;

Styled.IntroContinueButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 14px 28px;
  border: 0;
  border-radius: 10px;
  background: #2563EB;
  color: #FFFFFF;
  font-family: 'Mukta Vaani', Lato, sans-serif;
  font-weight: 700;
  font-size: 18px;
  cursor: pointer;
  transition: background 200ms ease;

  &:hover {
    background: #1D4ED8;
  }

  &:focus-visible {
    outline: 2px solid #2563EB;
    outline-offset: 2px;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

Styled.Timeline = styled.ol`
  position: relative;
  list-style: none;
  margin: 0 auto;
  padding: 0;
  max-width: 1180px;
`;

/* Each Step draws its own segment of the connector line via a
   ::before. Math:
     - Step has padding-top: 10px, circle is 40px → circle spans
       y=10..50, center at y=30 within the step.
     - Step has padding-left: 6px, circle is 40px → circle center
       sits 26px in. Line is 2px wide → left: 25px keeps centered.
   First step: clipped above its circle (top: 30px).
   Last step:  clipped at its circle's center (bottom anchored 30px
                from the step's top).
   The result is a single visually continuous line that begins at
   circle 1's center and ends at the last circle's center,
   regardless of how tall each step is. */
Styled.Step = styled.li`
  position: relative;
  display: grid;
  grid-template-columns: 40px 1fr;
  gap: 18px;
  padding: 10px 14px 10px 6px;
  margin-bottom: 12px;
  border-radius: 14px;
  background: transparent;
  transition: background 200ms ease;

  &::before {
    content: '';
    position: absolute;
    left: 25px;
    width: 2px;
    top: 0;
    /* Extend 12px past the step's bottom edge so the connector
       bridges the inter-step margin gap. The last step overrides
       this so the line still stops at circle 3's center. */
    bottom: -12px;
    background: #E2E8F0;
    z-index: 0;
  }

  &:first-child::before {
    top: 30px;
  }

  &:last-child::before {
    bottom: auto;
    height: 30px;
  }

  ${p => p.active && css`
    background: #DBEAFE;
  `}

  &:last-child {
    margin-bottom: 0;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const stepCircleBase = `
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-family: 'Mukta Vaani', Lato, sans-serif;
  font-size: 18px;
  font-weight: 700;
  background: #FFFFFF;
  border: 2px solid #E2E8F0;
  color: #64748B;
  position: relative;
  z-index: 1;
  padding: 0;
  transition: background 200ms ease, border-color 200ms ease, color 200ms ease;

  &:focus-visible {
    outline: 2px solid #2563EB;
    outline-offset: 2px;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

Styled.StepCircleButton = styled.button`
  ${stepCircleBase}
  cursor: pointer;

  ${p => p.completed && css`
    background: #10B981;
    border-color: #10B981;
    color: #FFFFFF;
  `}

  ${p => (!p.completed && p.active) && css`
    background: #EFF6FF;
    border-color: #2563EB;
    color: #2563EB;
  `}
`;

Styled.StepCircleStatic = styled.div`
  ${stepCircleBase}

  ${p => p.completed && css`
    background: #10B981;
    border-color: #10B981;
    color: #FFFFFF;
  `}

  ${p => (!p.completed && p.active) && css`
    background: #EFF6FF;
    border-color: #2563EB;
    color: #2563EB;
  `}
`;

Styled.StepBody = styled.div`
  padding-top: 2px;
`;

// Clickable wrapper around a parent step's title + description that
// toggles its substeps open/closed. Rendered as div+role=button so it
// can legally contain the existing <h2> and <p> children (a real
// <button> can't nest those).
Styled.StepHeaderToggle = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
  cursor: pointer;
  border-radius: 8px;

  &:focus-visible {
    outline: 2px solid #2563EB;
    outline-offset: 2px;
  }
`;

Styled.StepHeaderTextGroup = styled.div`
  flex: 1 1 auto;
  min-width: 0;
`;

// Chevron rendered next to a collapsible step header. Colored/sized to
// read as a UI affordance without competing with the title text.
Styled.StepHeaderChevron = styled.span`
  flex: 0 0 auto;
  align-self: center;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  color: #64748B;
  font-size: 14px;
  line-height: 1;
  transition: color 150ms ease;

  ${Styled.StepHeaderToggle}:hover & {
    color: #0F172A;
  }
`;

// Small, muted annotation that sits inline next to the step title
// (used for "est. time" callouts). Lighter weight + smaller size so
// it reads as metadata rather than part of the title.
Styled.StepTimeEstimate = styled.span`
  margin-left: 10px;
  font-family: 'Mukta Vaani', Lato, sans-serif;
  font-size: 13px;
  font-weight: 500;
  color: #64748B;
  letter-spacing: 0;
`;

Styled.StepTitle = styled.h2`
  margin: 0 0 4px;
  font-family: 'Mukta Vaani', Lato, sans-serif;
  font-size: 18px;
  font-weight: 800;
  color: #0F172A;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: color 300ms ease, text-decoration-color 300ms ease;

  ${p => p.completed && css`
    color: #94A3B8;
    text-decoration: line-through;
    text-decoration-color: #CBD5E1;
  `}

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

Styled.StepDescription = styled.p`
  margin: 0 0 6px;
  font-size: 16px;
  line-height: 1.45;
  color: #475569;
  max-width: 900px;
`;

Styled.InlineButton = styled.button`
  background: transparent;
  border: 0;
  padding: 0;
  margin: 0;
  font: inherit;
  color: #2563EB;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }

  &:focus-visible {
    outline: 2px solid #2563EB;
    outline-offset: 2px;
    border-radius: 4px;
  }
`;

Styled.StepLink = styled.a`
  display: inline-block;
  font-size: 15px;
  font-weight: 700;
  color: #2563EB;
  text-decoration: none;
  background: transparent;
  border: 0;
  padding: 0;
  cursor: pointer;
  font-family: inherit;

  &:hover {
    text-decoration: underline;
  }

  &:focus-visible {
    outline: 2px solid #2563EB;
    outline-offset: 2px;
    border-radius: 4px;
  }
`;

Styled.SubstepGroup = styled.div`
  margin-top: 8px;
  background: #F8FAFC;
  border: 1px solid #E2E8F0;
  border-radius: 12px;
  padding: 4px;
`;

Styled.SubstepRow = styled.button`
  display: grid;
  grid-template-columns: 24px 1fr;
  gap: 12px;
  align-items: flex-start;
  width: 100%;
  text-align: left;
  background: transparent;
  border: 0;
  border-radius: 8px;
  padding: 8px 12px;
  cursor: pointer;
  font-family: inherit;
  color: inherit;
  transition: background 150ms ease;

  & + & {
    border-top: 1px solid #E2E8F0;
  }

  &:hover {
    background: #FFFFFF;
  }

  &:focus-visible {
    outline: 2px solid #2563EB;
    outline-offset: -2px;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

Styled.Checkbox = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  margin-top: 1px;
  border: 2px solid #CBD5E1;
  border-radius: 5px;
  background: #FFFFFF;
  color: #FFFFFF;
  font-size: 13px;
  font-weight: 700;
  line-height: 1;
  transition: background 200ms ease, border-color 200ms ease;

  ${p => p.checked && css`
    background: #10B981;
    border-color: #10B981;
  `}

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

Styled.SubstepBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

Styled.SubstepTitle = styled.span`
  font-size: 18px;
  font-weight: 700;
  color: #0F172A;
  transition: color 300ms ease, text-decoration-color 300ms ease;

  ${p => p.completed && css`
    color: #94A3B8;
    text-decoration: line-through;
    text-decoration-color: #CBD5E1;
  `}

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

Styled.SubstepDescription = styled.span`
  font-size: 16px;
  line-height: 1.4;
  color: #475569;
`;

Styled.SubstepLink = styled.a`
  align-self: flex-start;
  font-size: 16px;
  font-weight: 700;
  color: #2563EB;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }

  &:focus-visible {
    outline: 2px solid #2563EB;
    outline-offset: 2px;
    border-radius: 4px;
  }
`;

Styled.CompletionBanner = styled.div`
  max-width: 760px;
  margin: 32px auto 0;
  padding: 28px 32px;
  border-radius: 20px;
  background: linear-gradient(135deg, #ECFDF5, #D1FAE5);
  border: 1px solid #A7F3D0;
  display: grid;
  grid-template-columns: 56px 1fr;
  gap: 20px;
  align-items: center;
  animation: ${fadeUp} 500ms ease both;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

Styled.CompletionIcon = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: #10B981;
  color: #FFFFFF;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 28px;
  font-weight: 700;
`;

Styled.CompletionTitle = styled.h3`
  margin: 0 0 4px;
  font-family: 'Mukta Vaani', Lato, sans-serif;
  font-size: 22px;
  font-weight: 800;
  color: #064E3B;
`;

Styled.CompletionDescription = styled.p`
  margin: 0;
  font-size: 15px;
  color: #047857;
  line-height: 1.5;
`;

Styled.SrOnly = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;

// ---------- popup shell -------------------------------------------

Styled.Modal = {};

Styled.Modal.Backdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px;
  z-index: 1000;
  animation: ${fadeIn} 200ms ease both;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

Styled.Modal.Card = styled.div`
  position: relative;
  background: #ffffff;
  border-radius: 16px;
  width: 100%;
  max-width: 640px;
  max-height: 86vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.25);
  font-family: 'Mukta Vaani', Lato, sans-serif;
  overflow: hidden;
`;

Styled.Modal.HeaderBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 18px 24px;
  border-bottom: 1px solid #E2E8F0;
`;

Styled.Modal.Title = styled.h2`
  margin: 0;
  font-family: 'Mukta Vaani', Lato, sans-serif;
  font-size: 20px;
  font-weight: 800;
  color: #0F172A;
`;

Styled.Modal.TitleStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

Styled.Modal.Subtitle = styled.span`
  font-family: 'Mukta Vaani', Lato, sans-serif;
  font-size: 13px;
  font-weight: 500;
  color: rgba(0, 27, 59, 0.6);
`;

Styled.Modal.HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

Styled.Modal.DownloadButton = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  border-radius: 8px;
  background: #2563EB;
  color: #ffffff;
  font-size: 14px;
  font-weight: 700;
  text-decoration: none;

  &:hover {
    background: #1D4ED8;
  }
`;

Styled.Modal.CloseButton = styled.button`
  background: transparent;
  border: 0;
  padding: 6px;
  border-radius: 8px;
  cursor: pointer;
  color: #64748B;
  font-size: 22px;
  line-height: 1;

  &:hover {
    background: #F1F5F9;
    color: #0F172A;
  }
`;

Styled.Modal.Body = styled.div`
  overflow-y: auto;
  padding: 24px 28px;
  font-size: 16px;
  line-height: 1.55;
  color: #1F2A44;
`;

// ---------- teammates popup ---------------------------------------

Styled.Roster = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 4px;
`;

Styled.RosterRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
`;

Styled.RosterName = styled.div`
  font-family: 'Mukta Vaani', Lato, sans-serif;
  font-weight: 500;
  font-size: 16px;
  color: rgba(0, 27, 59, 0.94);
  flex: 0 0 auto;
`;

Styled.RosterEmail = styled.div`
  flex: 1 1 auto;
  height: 44px;
  background: #ffffff;
  border: 1px solid #d5d9dd;
  border-radius: 8px;
  font-family: 'Mukta Vaani', Lato, sans-serif;
  font-weight: 500;
  font-size: 15px;
  line-height: 44px;
  color: rgba(0, 27, 59, 0.6);
  padding: 0 64px 0 16px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  position: relative;
`;

Styled.RosterCopyButton = styled.button`
  position: absolute;
  top: 0;
  right: 0;
  width: 52px;
  height: 100%;
  border: 0;
  background: #5C91F5;
  border-radius: 0 8px 8px 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s ease;

  &:hover {
    background: #74A1F6;
  }

  img {
    width: 18px;
    height: 18px;
  }
`;

Styled.CopyAllRow = styled.div`
  text-align: right;
  margin-top: 20px;
`;

Styled.CopyAllLink = styled.a`
  font-family: 'Mukta Vaani', Lato, sans-serif;
  font-weight: 600;
  font-size: 15px;
  color: #5C91F5;
  text-decoration: none;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

Styled.Toast = styled.div`
  position: fixed;
  bottom: 32px;
  left: 50%;
  transform: translateX(-50%);
  background: #0F172A;
  color: #ffffff;
  padding: 10px 18px;
  border-radius: 999px;
  font-size: 14px;
  z-index: 1100;
  animation: ${fadeIn} 150ms ease both;
`;

Styled.Empty = styled.div`
  padding: 32px 0;
  text-align: center;
  color: #64748B;
`;

// ---------- contract popup ----------------------------------------

Styled.ContractIntro = styled.p`
  margin: 0 0 16px;
  font-size: 16px;
  line-height: 1.55;
  color: #1F2A44;
`;

Styled.ContractList = styled.ol`
  margin: 0 0 16px;
  padding: 0 0 0 24px;
  text-align: left;

  li {
    font-size: 16px;
    line-height: 1.55;
    color: #1F2A44;
    margin-bottom: 4px;
  }
`;

Styled.ContractCallout = styled.p`
  margin: 0 0 24px;
  font-size: 16px;
  line-height: 1.55;
  color: #1F2A44;
  font-style: italic;
`;

// Sections (Expectations, Team Communication, etc.) left-align
// with the intro paragraph above. h3 and ol both reset their
// browser-default left padding/margin; the list gets its own
// padding-left so the "A.", "B." markers sit inside the visible
// content area instead of being pushed off into negative space.
Styled.ContractSection = styled.section`
  margin: 0 0 24px;
  padding: 0;
  text-align: left;

  &:last-child {
    margin-bottom: 0;
  }
`;

Styled.ContractSectionTitle = styled.h3`
  margin: 0 0 10px;
  padding: 0;
  text-align: left;
  font-family: 'Mukta Vaani', Lato, sans-serif;
  font-size: 17px;
  font-weight: 800;
  color: #2563EB;
`;

Styled.ContractSectionList = styled.ol`
  margin: 0;
  padding: 0 0 0 24px;
  text-align: left;
  list-style: upper-alpha;

  li {
    margin-bottom: 6px;
    font-size: 16px;
    line-height: 1.55;
    color: #1F2A44;
  }
`;

// ---------- component ---------------------------------------------

const TEAMMATES_API = `${process.env.REACT_APP_API_URL}v2/games/teamplayers/`;

class Round1Checklist extends PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      completed: this.readFromStorage(),
      teammatesPopupOpen: false,
      contractPopupOpen: false,
      teammates: [],
      teammatesLoading: false,
      teammatesLoaded: false,
      toastVisible: false,
      // Which parent steps are collapsed. Keyed by step id; absence
      // means expanded. Sub-sections start expanded by default so
      // users see the substeps immediately. In-memory only — resets
      // on reload.
      collapsedSteps: {},
      // Role-intro screen is shown before the checklist on first
      // visit. Dismissal persists per user + role via localStorage.
      roleIntroDismissed: this.readRoleIntroDismissed(),
    };
  }

  readRoleIntroDismissed = () => isRoleIntroDismissed();

  dismissRoleIntro = () => {
    try {
      window.localStorage.setItem(roleIntroStorageKey(), '1');
    } catch (e) {
      // Safe fallback — dismissal still applies in-memory for this
      // session even if storage is blocked.
    }
    // Notify listeners (StudentLeftBar hides non-Welcome nav until
    // this fires — that gate lifts as soon as the user clicks
    // Continue on the role-intro screen).
    EventEmitter.getInstance().trigger(ROLE_INTRO_DISMISSED_EVENT);
    this.setState({ roleIntroDismissed: true });
  };

  toggleStepCollapse = (stepId) => {
    this.setState((prev) => {
      const next = { ...prev.collapsedSteps };
      if (next[stepId]) delete next[stepId];
      else next[stepId] = true;
      return { collapsedSteps: next };
    });
  };

  isStepCollapsed = (stepId) => !!this.state.collapsedSteps[stepId];

  handleStepHeaderKeyDown = (stepId) => (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      this.toggleStepCollapse(stepId);
    }
  };

  componentDidMount() {
    this.overrideParentBackgrounds();
    if (typeof window !== 'undefined') {
      window.addEventListener('keydown', this.handleKeydown);
    }
  }

  componentWillUnmount() {
    this.restoreParentBackgrounds();
    if (typeof window !== 'undefined') {
      window.removeEventListener('keydown', this.handleKeydown);
    }
    if (this._toastTimer) {
      clearTimeout(this._toastTimer);
      this._toastTimer = null;
    }
  }

  setRootEl = (el) => {
    this._rootEl = el;
  };

  // Esc closes any open popup.
  handleKeydown = (e) => {
    if (e.key !== 'Escape') return;
    if (this.state.teammatesPopupOpen || this.state.contractPopupOpen) {
      this.closeAllPopups();
    }
  };

  overrideParentBackgrounds = () => {
    if (typeof window === 'undefined' || !this._rootEl) return;
    this._modifiedNodes = [];
    let node = this._rootEl.parentElement;
    let hops = 0;
    while (node && hops < 6) {
      const cs = window.getComputedStyle(node);
      if (cs.backgroundColor === 'rgb(245, 246, 247)') {
        this._modifiedNodes.push({
          el: node,
          originalBg: node.style.background,
        });
        node.style.background = '#FAFBFD';
      }
      node = node.parentElement;
      hops += 1;
    }
  };

  restoreParentBackgrounds = () => {
    if (this._modifiedNodes) {
      this._modifiedNodes.forEach((entry) => {
        entry.el.style.background = entry.originalBg;
      });
      this._modifiedNodes = null;
    }
  };

  // ---- persistence -----------------------------------------------

  readFromStorage = () => {
    try {
      const raw = window.localStorage.getItem(storageKey());
      if (!raw) return {};
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return {};
      const next = {};
      parsed.forEach((id) => {
        if (typeof id === 'string') next[id] = true;
      });
      return next;
    } catch (e) {
      return {};
    }
  };

  writeToStorage = (completed) => {
    try {
      const ids = Object.keys(completed).filter(k => completed[k] === true);
      window.localStorage.setItem(storageKey(), JSON.stringify(ids));
    } catch (e) {
      // localStorage unavailable — silent. In-memory state still drives
      // this render, we just lose persistence across reloads.
    }
  };

  toggle = (id) => {
    this.setState((prev) => {
      const next = { ...prev.completed };
      if (next[id]) {
        delete next[id];
      } else {
        next[id] = true;
      }
      this.writeToStorage(next);
      return { completed: next };
    });
  };

  // ---- derived helpers -------------------------------------------

  get activeRound() {
    const { roundInUrl, currentRound } = this.props;
    return roundInUrl || currentRound || 1;
  }

  countableCompleted = () => {
    const { completed } = this.state;
    return COUNTABLE_IDS.filter(id => completed[id]).length;
  };

  isConductResearchComplete = () => {
    const { completed } = this.state;
    return (
      completed['review-case-study']
      && completed['review-round-goals']
      && completed['analyze-customers']
    ) ? true : false;
  };

  isLeafStepComplete = (id) => {
    const { completed } = this.state;
    return !!completed[id];
  };

  // First incomplete top-level step drives the blue active highlight.
  firstIncompleteStepId = () => {
    if (!this.isLeafStepComplete('team-outreach')) return 'team-outreach';
    if (!this.isConductResearchComplete()) return 'conduct-research';
    if (!this.isLeafStepComplete('make-decisions')) return 'make-decisions';
    return null;
  };

  // ---- popup actions ---------------------------------------------

  openTeammatesPopup = () => {
    this.setState({ teammatesPopupOpen: true });
    this.fetchTeammates();
  };

  openContractPopup = () => {
    this.setState({ contractPopupOpen: true });
  };

  closeAllPopups = () => {
    this.setState({ teammatesPopupOpen: false, contractPopupOpen: false });
  };

  fetchTeammates = () => {
    if (this.state.teammatesLoaded || this.state.teammatesLoading) return;
    this.setState({ teammatesLoading: true });
    getRequest(`${TEAMMATES_API}?game=${Auth.getGameId()}`)
      .then(res => {
        const body = (res && res.body) || {};
        const rows = Array.isArray(body.results) ? body.results : [];
        const myUserId = Number(Auth.getUserId());
        const currentTeam = Teams.currentTeam();
        const myTeamId = currentTeam ? Number(currentTeam.id) : null;
        // Filter to teammates on the current team, excluding self.
        const teammates = rows
          .filter(r => r && r.user)
          .filter(r => myTeamId == null || Number(r.team) === myTeamId)
          .filter(r => Number(r.user.id) !== myUserId)
          .map(r => ({
            fullname: `${r.user.first_name || ''} ${r.user.last_name || ''}`.trim()
              || r.user.username
              || 'Teammate',
            email: r.user.email || '',
          }));

        this.setState({
          teammates,
          teammatesLoading: false,
          teammatesLoaded: true,
        });
      })
      .catch(() => {
        this.setState({ teammatesLoading: false, teammatesLoaded: true });
      });
  };

  showToast = () => {
    if (this._toastTimer) clearTimeout(this._toastTimer);
    this.setState({ toastVisible: true });
    this._toastTimer = setTimeout(() => {
      this.setState({ toastVisible: false });
      this._toastTimer = null;
    }, 1800);
  };

  copyEmail = (email) => {
    if (!email) return;
    if (navigator && navigator.clipboard) {
      navigator.clipboard.writeText(email).then(this.showToast).catch(() => {});
    }
  };

  copyAllEmails = (e) => {
    if (e) e.preventDefault();
    const emails = this.state.teammates.map(t => t.email).filter(Boolean).join('\n');
    this.copyEmail(emails);
  };

  // ---- URL builders ----------------------------------------------

  withRound = (path) => {
    return `${path}${path.indexOf('?') >= 0 ? '&' : '?'}round=${this.activeRound}`;
  };

  customerAnalysisUrl = () => {
    const role = getRoleForAnalysisRoute();
    return this.withRound(`/${role}/analysis/market`);
  };

  // Click handler shared by step + substep rows. Stops the event
  // from toggling the parent step when an inline link is clicked.
  stopRowClickPropagation = (e) => {
    if (e) e.stopPropagation();
  };

  // ---- render ----------------------------------------------------

  renderStepCircle = (step, completed, active) => {
    const label = completed
      ? `Mark ${step.title} as incomplete`
      : `Mark ${step.title} as complete`;
    const content = completed ? '✓' : step.number;

    if (step.id === 'conduct-research') {
      return (
        <Styled.StepCircleStatic
          completed={completed}
          active={active}
          aria-hidden="true"
        >
          {content}
        </Styled.StepCircleStatic>
      );
    }

    return (
      <Styled.StepCircleButton
        type="button"
        onClick={() => this.toggle(step.id)}
        aria-pressed={completed}
        aria-label={label}
        completed={completed}
        active={active}
      >
        {content}
      </Styled.StepCircleButton>
    );
  };

  renderTeamOutreachDescription = () => (
    <Styled.StepDescription>
      Introduce yourself to your teammates{' '}
      <Styled.InlineButton
        type="button"
        onClick={this.openTeammatesPopup}
        aria-haspopup="dialog"
      >
        (view teammates)
      </Styled.InlineButton>
      , schedule a meeting, and align on how you'll work together this round.
      Create a team contract to enhance collaboration.{' '}
      <Styled.StepLink
        as="button"
        type="button"
        onClick={this.openContractPopup}
        aria-haspopup="dialog"
      >
        View the team contract template →
      </Styled.StepLink>
    </Styled.StepDescription>
  );

  renderInternalLink = (label, href) => (
    <Link to={href} style={{ textDecoration: 'none' }}>
      <Styled.StepLink as="span">
        {label} →
      </Styled.StepLink>
    </Link>
  );

  renderSubsteps = () => {
    const { completed } = this.state;
    const substeps = [
      {
        id: 'review-case-study',
        title: 'Review Case Study',
        description:
          'Gain a general understanding of the market and how it might impact each of the core business functions.',
        linkText: 'Open case study',
        linkHref: this.withRound('/analysis/bcase/executive-summary'),
      },
      {
        id: 'review-round-goals',
        title: 'Review Round Goals',
        description:
          'Understand the 4 financial metrics and targets that you need to hit each round to earn a 100% score.',
        linkText: 'View round goals',
        linkHref: this.withRound('/goals-and-targets'),
      },
      {
        id: 'analyze-customers',
        title: 'Analyze Customers',
        description:
          'Identify which customers to target to reach your financial goals.',
        linkText: 'Open customer analysis',
        linkHref: this.customerAnalysisUrl(),
      },
    ];

    return (
      <Styled.SubstepGroup>
        {substeps.map((sub) => {
          const isDone = !!completed[sub.id];
          return (
            <Styled.SubstepRow
              key={sub.id}
              type="button"
              onClick={() => this.toggle(sub.id)}
              aria-pressed={isDone}
              aria-label={isDone
                ? `Mark ${sub.title} as incomplete`
                : `Mark ${sub.title} as complete`}
            >
              <Styled.Checkbox checked={isDone} aria-hidden="true">
                {isDone ? '✓' : ''}
              </Styled.Checkbox>
              <Styled.SubstepBody>
                <Styled.SubstepTitle completed={isDone}>
                  {sub.title}
                </Styled.SubstepTitle>
                <Styled.SubstepDescription>
                  {sub.description}{' '}
                  <Link
                    to={sub.linkHref}
                    onClick={this.stopRowClickPropagation}
                    style={{ textDecoration: 'none' }}
                  >
                    <Styled.SubstepLink as="span">
                      {sub.linkText} →
                    </Styled.SubstepLink>
                  </Link>
                </Styled.SubstepDescription>
              </Styled.SubstepBody>
            </Styled.SubstepRow>
          );
        })}
      </Styled.SubstepGroup>
    );
  };

  renderTeammatesPopup = () => {
    const { teammates, teammatesLoading, teammatesLoaded } = this.state;
    const currentTeam = Teams.currentTeam();
    const teamLabel = formatTeamName(currentTeam && currentTeam.name);
    return (
      <Styled.Modal.Backdrop onClick={this.closeAllPopups} role="dialog" aria-modal="true">
        <Styled.Modal.Card onClick={e => e.stopPropagation()}>
          <Styled.Modal.HeaderBar>
            <Styled.Modal.TitleStack>
              <Styled.Modal.Title>Your Teammates</Styled.Modal.Title>
              {teamLabel && (
                <Styled.Modal.Subtitle>{teamLabel}</Styled.Modal.Subtitle>
              )}
            </Styled.Modal.TitleStack>
            <Styled.Modal.HeaderActions>
              <Styled.Modal.CloseButton
                type="button"
                onClick={this.closeAllPopups}
                aria-label="Close"
              >
                ×
              </Styled.Modal.CloseButton>
            </Styled.Modal.HeaderActions>
          </Styled.Modal.HeaderBar>
          <Styled.Modal.Body>
            {teammatesLoading && !teammatesLoaded && (
              <Styled.Empty>Loading teammates…</Styled.Empty>
            )}
            {teammatesLoaded && teammates.length === 0 && (
              <Styled.Empty>No teammates found.</Styled.Empty>
            )}
            {teammates.length > 0 && (
              <div>
                <Styled.Roster>
                  {teammates.map((t, idx) => (
                    <Styled.RosterRow key={idx}>
                      <Styled.RosterName>{t.fullname}</Styled.RosterName>
                      <Styled.RosterEmail>
                        {t.email}
                        <Styled.RosterCopyButton
                          type="button"
                          onClick={() => this.copyEmail(t.email)}
                          aria-label={`Copy ${t.fullname}'s email`}
                        >
                          <img src={CopyIcon} alt="" />
                        </Styled.RosterCopyButton>
                      </Styled.RosterEmail>
                    </Styled.RosterRow>
                  ))}
                </Styled.Roster>
                <Styled.CopyAllRow>
                  <Styled.CopyAllLink onClick={this.copyAllEmails}>
                    Copy All Emails
                  </Styled.CopyAllLink>
                </Styled.CopyAllRow>
              </div>
            )}
          </Styled.Modal.Body>
        </Styled.Modal.Card>
      </Styled.Modal.Backdrop>
    );
  };

  renderContractPopup = () => (
    <Styled.Modal.Backdrop onClick={this.closeAllPopups} role="dialog" aria-modal="true">
      <Styled.Modal.Card onClick={e => e.stopPropagation()}>
        <Styled.Modal.HeaderBar>
          <Styled.Modal.Title>Team Contract Template</Styled.Modal.Title>
          <Styled.Modal.HeaderActions>
            <Styled.Modal.DownloadButton
              href={TEAM_CONTRACT_PDF_URL}
              download="Market_Games_Team_Contract.pdf"
              target="_blank"
              rel="noopener noreferrer"
            >
              ⬇ Download
            </Styled.Modal.DownloadButton>
            <Styled.Modal.CloseButton
              type="button"
              onClick={this.closeAllPopups}
              aria-label="Close"
            >
              ×
            </Styled.Modal.CloseButton>
          </Styled.Modal.HeaderActions>
        </Styled.Modal.HeaderBar>
        <Styled.Modal.Body>
          <Styled.ContractIntro>
            A team contract is a set of basic principles and standards for working
            together to create a supportive and productive team environment. This
            team contract covers the following:
          </Styled.ContractIntro>
          <Styled.ContractList>
            <li>Expectations of one another</li>
            <li>Meeting planning</li>
            <li>Team communication and participation norms (approach to problem-solving, decision-making, conflict management)</li>
            <li>Responsibilities to one another</li>
          </Styled.ContractList>
          <Styled.ContractCallout>
            It is recommended that teams revisit their Contract at each team
            meeting to ensure they are living up to their commitments and can
            course-correct as needed.
          </Styled.ContractCallout>

          <Styled.ContractSection>
            <Styled.ContractSectionTitle>Expectations</Styled.ContractSectionTitle>
            <Styled.ContractSectionList>
              <li>Work collaboratively on this team project.</li>
              <li>Attend team meetings and arrive on time. Whenever possible, meet in person; when meeting remotely, cameras on.</li>
              <li>Communicate effectively and respond to teammates quickly.</li>
              <li>Support each other's learning. Give each other the benefit of the doubt.</li>
              <li>See the simulation through to completion.</li>
              <li>Don't be evil.</li>
            </Styled.ContractSectionList>
          </Styled.ContractSection>

          <Styled.ContractSection>
            <Styled.ContractSectionTitle>Team Meeting Planning &amp; Attendance</Styled.ContractSectionTitle>
            <Styled.ContractSectionList>
              <li>Meetings will be held every ____ days/weeks (pick a day and time and set that schedule up front).</li>
              <li>Agendas will be issued every ____ days in advance by _____________ (since sim roles rotate, pick a role in the sim that will be responsible for the agenda).</li>
              <li>Team members will come to the meetings prepared.</li>
              <li>Agenda items for the next meeting will be discussed at the end of each meeting.</li>
              <li>Unresolved issues will be added to the Issues list.</li>
              <li>If a team member cannot attend a meeting, they will let team members know well in advance (if possible).</li>
              <li>Meeting tasks will be rotated among members.</li>
            </Styled.ContractSectionList>
          </Styled.ContractSection>

          <Styled.ContractSection>
            <Styled.ContractSectionTitle>Team Communication</Styled.ContractSectionTitle>
            <Styled.ContractSectionList>
              <li>Be responsive to team member requests and outreach. Agree and stick to a turnaround time for responding.</li>
              <li>Practice active, effective listening skills. Playback what you heard to ensure it is what was intended.</li>
              <li>Seek first to understand, and then to be understood.</li>
              <li>Be clear and to the point.</li>
              <li>Keep discussions on track. Consider appointing a time keeper to keep team meetings focused and moving.</li>
            </Styled.ContractSectionList>
          </Styled.ContractSection>

          <Styled.ContractSection>
            <Styled.ContractSectionTitle>Team Participation</Styled.ContractSectionTitle>
            <Styled.ContractSectionList>
              <li>Be honest and open during meetings.</li>
              <li>Encourage a diversity of opinions on all topics.</li>
              <li>Give everyone the opportunity for equal participation.</li>
              <li>Be open to new approaches and listen to new ideas.</li>
              <li>Avoid placing blame when things go wrong. Instead, discuss the process and explore how it can be improved next time.</li>
            </Styled.ContractSectionList>
          </Styled.ContractSection>

          <Styled.ContractSection>
            <Styled.ContractSectionTitle>Team Problem-Solving</Styled.ContractSectionTitle>
            <Styled.ContractSectionList>
              <li>Build on each other's ideas.</li>
              <li>Whenever possible, use data to assist in problem-solving.</li>
              <li>Remember that solving problems is a creative process. New ideas and new understandings often result.</li>
            </Styled.ContractSectionList>
          </Styled.ContractSection>

          <Styled.ContractSection>
            <Styled.ContractSectionTitle>Team Decision-Making</Styled.ContractSectionTitle>
            <Styled.ContractSectionList>
              <li>Make decisions based on data whenever feasible.</li>
              <li>Seek to find the needed information or data.</li>
              <li>Discuss criteria (cost, time, impact, etc.) for making a decision before choosing an option.</li>
              <li>Encourage and explore different interpretations of data.</li>
              <li>Get input from the entire team before a decision is made.</li>
            </Styled.ContractSectionList>
          </Styled.ContractSection>

          <Styled.ContractSection>
            <Styled.ContractSectionTitle>Handling Conflict</Styled.ContractSectionTitle>
            <Styled.ContractSectionList>
              <li>View conflict as normal and as an opportunity for growth.</li>
              <li>Seek to understand the interests and desires of each party involved before arriving at answers or solutions.</li>
              <li>Listen openly to other points of view.</li>
              <li>Repeat what we understand to the other person and ask if it is correct.</li>
              <li>Acknowledge valid points that the other person has made.</li>
              <li>State our points of view and our interests in a non-judgmental and non-attacking manner.</li>
              <li>Seek to find some common ground for agreement.</li>
            </Styled.ContractSectionList>
          </Styled.ContractSection>
        </Styled.Modal.Body>
      </Styled.Modal.Card>
    </Styled.Modal.Backdrop>
  );

  renderRoleIntro = (roleMeta) => (
    <Styled.Root innerRef={this.setRootEl}>
      <Styled.Header>
        <Styled.Title>Welcome to the Intro to Business Challenge!</Styled.Title>
      </Styled.Header>
      <Styled.IntroCard>
        <Styled.IntroSubhead>
          Each round you'll take on a different role. Here's what you're
          responsible for this round.
        </Styled.IntroSubhead>
        <Styled.RoleBadge>Your Role: {roleMeta.label}</Styled.RoleBadge>
        <Styled.IntroDescription>{roleMeta.description}</Styled.IntroDescription>
        <Styled.IntroContinueButton
          type="button"
          onClick={this.dismissRoleIntro}
        >
          Continue →
        </Styled.IntroContinueButton>
      </Styled.IntroCard>
    </Styled.Root>
  );

  render() {
    // Role-intro screen precedes the checklist. Only rendered when
    // the user's role maps to known copy — unknown roles fall
    // straight through to the checklist to avoid a blank state.
    if (!this.state.roleIntroDismissed) {
      const roleMeta = ROLE_INTRO_CONTENT[getCurrentRoleCode()];
      if (roleMeta) {
        return this.renderRoleIntro(roleMeta);
      }
    }

    const completedCount = this.countableCompleted();
    const pct = Math.round((completedCount / TOTAL_COUNTABLE) * 100);
    const activeId = this.firstIncompleteStepId();
    const allDone = completedCount === TOTAL_COUNTABLE;

    const conductComplete = this.isConductResearchComplete();
    const teamOutreachComplete = this.isLeafStepComplete('team-outreach');
    const makeDecisionsComplete = this.isLeafStepComplete('make-decisions');

    return (
      <Styled.Root innerRef={this.setRootEl}>
        <Styled.Header>
          <Styled.Title>Welcome to the Intro to Business Challenge!</Styled.Title>
          <Styled.Subhead>
            To get started for the round, follow the checklist below.
          </Styled.Subhead>
          {/* Visible progress bar removed at product's request — the
              sr-only live region still announces progress to
              screen readers as items get checked off. */}
          <Styled.SrOnly aria-live="polite">
            {completedCount} of {TOTAL_COUNTABLE} steps complete
          </Styled.SrOnly>
        </Styled.Header>

        <Styled.Timeline>
          {/* Step 1 — Team Outreach */}
          <Styled.Step active={activeId === 'team-outreach'}>
            {this.renderStepCircle(
              { id: 'team-outreach', number: 1, title: 'Team Outreach' },
              teamOutreachComplete,
              activeId === 'team-outreach',
            )}
            <Styled.StepBody>
              <Styled.StepTitle completed={teamOutreachComplete}>
                Team Outreach
              </Styled.StepTitle>
              {this.renderTeamOutreachDescription()}
            </Styled.StepBody>
          </Styled.Step>

          {/* Step 2 — Conduct Research (parent-only, collapsible) */}
          <Styled.Step active={activeId === 'conduct-research'}>
            {this.renderStepCircle(
              { id: 'conduct-research', number: 2, title: 'Conduct Research' },
              conductComplete,
              activeId === 'conduct-research',
            )}
            <Styled.StepBody>
              <Styled.StepHeaderToggle
                role="button"
                tabIndex={0}
                onClick={() => this.toggleStepCollapse('conduct-research')}
                onKeyDown={this.handleStepHeaderKeyDown('conduct-research')}
                aria-expanded={!this.isStepCollapsed('conduct-research')}
                aria-controls="substeps-conduct-research"
                aria-label={this.isStepCollapsed('conduct-research')
                  ? 'Expand Conduct Research'
                  : 'Collapse Conduct Research'}
              >
                <Styled.StepHeaderTextGroup>
                  <Styled.StepTitle completed={conductComplete}>
                    <span>Conduct Research</span>
                    <Styled.StepTimeEstimate>(est. time 1-2 hrs)</Styled.StepTimeEstimate>
                  </Styled.StepTitle>
                  <Styled.StepDescription>
                    Do this on your own before the team meeting, three quick reviews
                    to come prepared.
                  </Styled.StepDescription>
                </Styled.StepHeaderTextGroup>
                <Styled.StepHeaderChevron aria-hidden="true">
                  {this.isStepCollapsed('conduct-research') ? '▸' : '▾'}
                </Styled.StepHeaderChevron>
              </Styled.StepHeaderToggle>
              {!this.isStepCollapsed('conduct-research') && (
                <div id="substeps-conduct-research">
                  {this.renderSubsteps()}
                </div>
              )}
            </Styled.StepBody>
          </Styled.Step>

          {/* Step 3 — Make Decisions */}
          <Styled.Step active={activeId === 'make-decisions'}>
            {this.renderStepCircle(
              { id: 'make-decisions', number: 3, title: 'Make Decisions' },
              makeDecisionsComplete,
              activeId === 'make-decisions',
            )}
            <Styled.StepBody>
              <Styled.StepTitle completed={makeDecisionsComplete}>
                Make Decisions
                <Styled.StepTimeEstimate>(est. time 1-1.5 hrs)</Styled.StepTimeEstimate>
              </Styled.StepTitle>
              <Styled.StepDescription>
                {getRoleDisplayName() && (
                  <span>
                    You're assigned the <strong>{getRoleDisplayName()}</strong> role this round.{' '}
                  </span>
                )}
                Meet with your team, in person or online, to make and post all
                team decisions before the round ends. That's it.{' '}
                <Link to={this.withRound('/decisions')} style={{ textDecoration: 'none' }}>
                  <Styled.StepLink as="span">
                    Go to decisions →
                  </Styled.StepLink>
                </Link>
              </Styled.StepDescription>
            </Styled.StepBody>
          </Styled.Step>
        </Styled.Timeline>

        {allDone && (
          <Styled.CompletionBanner>
            <Styled.CompletionIcon aria-hidden="true">
              {COMPLETION_COPY.icon}
            </Styled.CompletionIcon>
            <div>
              <Styled.CompletionTitle>{COMPLETION_COPY.title}</Styled.CompletionTitle>
              <Styled.CompletionDescription>
                {COMPLETION_COPY.description}
              </Styled.CompletionDescription>
            </div>
          </Styled.CompletionBanner>
        )}

        {this.state.teammatesPopupOpen && this.renderTeammatesPopup()}
        {this.state.contractPopupOpen && this.renderContractPopup()}
        {this.state.toastVisible && <Styled.Toast>Copied to clipboard</Styled.Toast>}
      </Styled.Root>
    );
  }
}

export default Round1Checklist;



// WEBPACK FOOTER //
// ./src/components/onboarding/welcome/Round1Checklist.js